import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../api/ResturantClient";
import { subscribeToOrderUpdates, disconnectSocket } from "./utils/socket";

function KitchenManagement() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const presentTime = new Date();
  const [currentTime, setCurrentTime] = useState(
    new Date(
      Date.UTC(
        presentTime.getUTCFullYear(),
        presentTime.getUTCMonth(),
        presentTime.getUTCDate(),
        presentTime.getUTCHours(),
        presentTime.getUTCMinutes(),
        presentTime.getUTCSeconds()
      )
    )
  );
  const restaurantId = sessionStorage.getItem("restaurant_id");
  const [processingOrders, setProcessingOrders] = useState(new Set());

  const fetchKitchenOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(
        `/kitchen/restaurant/${restaurantId}/orders`
      );
      setOrders(response.data);
    } catch (error) {
      setError("Failed to load orders. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchKitchenOrders();

    const handleOrderUpdate = (orderData) => {
      try {
        orderData = JSON.parse(orderData);
        console.log('Socket update received:', orderData);
        
        setOrders(prevOrders => {
          // Check if order already exists
          const existingOrderIndex = prevOrders.findIndex(
            (order) => order.order_id === orderData.order_id
          );
          
          if (existingOrderIndex >= 0) {
            // Update existing order
            const updatedOrders = [...prevOrders];
            updatedOrders[existingOrderIndex] = {
              ...updatedOrders[existingOrderIndex],
              ...orderData
            };
            return updatedOrders;
          } else {
            // Add new order at the beginning
            return [orderData, ...prevOrders];
          }
        });
      } catch (error) {
        console.error('Error processing socket update:', error);
      }
    };

    subscribeToOrderUpdates(restaurantId, handleOrderUpdate);
    
    return () => {
      disconnectSocket();
    };
  }, [restaurantId, fetchKitchenOrders]);

  useEffect(() => {
    const timerInterval = setInterval(() => {
      const now = new Date();
      const gmtTime = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          now.getUTCHours(),
          now.getUTCMinutes(),
          now.getUTCSeconds()
        )
      ).getTime();

      setCurrentTime(gmtTime);
    }, 1000);

    return () => clearInterval(timerInterval);
  }, []);

  const formatTimeDifference = (updatedAt) => {
    const orderTime = new Date(updatedAt).getTime();
    const diffInSeconds = Math.floor((currentTime - orderTime) / 1000);
    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    if (processingOrders.has(orderId)) return;

    setProcessingOrders((prev) => new Set(prev).add(orderId));
    
    try {
      // Optimistically update the UI immediately
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.order_id === orderId 
            ? { ...order, status: newStatus }
            : order
        )
      );

      const response = await apiClient.post(
        `/kitchen/restaurant/${restaurantId}/update-order-status/${orderId}`,
        { status: newStatus }
      );
      
      if (response.status === 200) {
        console.log(`Order ${orderId} status updated to ${newStatus}`);
        // The socket should handle the real update, but we'll refresh to be safe
        await fetchKitchenOrders();
      }
    } catch (error) {
      console.error(`Failed to update order status to ${newStatus}:`, error);
      // Revert optimistic update on error
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.order_id === orderId 
            ? { ...order, status: order.status } // Keep original status
            : order
        )
      );
      alert(`Failed to update order status to ${newStatus}. Please try again later.`);
    } finally {
      setProcessingOrders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Filter out duplicate orders (safety check)
  const uniqueOrders = orders.filter((order, index, self) => 
    index === self.findIndex(o => o.order_id === order.order_id)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100 py-4 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl shadow-lg p-2 mb-4 text-center text-white">
          <h2 className="text-2xl font-bold mb-2">Kitchen Management</h2>
          <p className="text-orange-100 text-sm">
            Monitor and manage order preparation in real-time
          </p>
          <div className="mt-2 text-xs text-orange-200">
            Orders: {uniqueOrders.length} | Processing: {processingOrders.size}
          </div>
        </div>

        {/* Guidance for restaurants using a printed ticket instead of a kitchen tablet */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-sm text-blue-800">
          <p className="font-semibold">Kitchen Status Dashboard</p>
          <p>
            Use this screen to update customer-visible order status after the
            kitchen begins preparing the printed ticket.
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-orange-500 mb-3"></div>
            <p className="text-gray-600 font-semibold">Loading kitchen orders...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl text-center shadow-md">
            {error}
          </div>
        ) : uniqueOrders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-orange-400 mb-3">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-600 text-lg font-semibold mb-1">No Orders in Kitchen</p>
            <p className="text-gray-500 text-sm">New orders will appear here when they are approved</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {uniqueOrders.map((order) => (
              <div
                key={order.order_id}
                className="bg-white rounded-xl shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-200 flex flex-col border border-gray-200"
              >
                {/* Order Header - Compact */}
                <div className={`p-3 ${
                  order.status === "Completed" 
                    ? "bg-gradient-to-r from-green-500 to-emerald-600" 
                    : order.status === "Ready"
                    ? "bg-gradient-to-r from-yellow-500 to-amber-600"
                    : "bg-gradient-to-r from-blue-500 to-indigo-600"
                } text-white`}>
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-lg">
                      #{order.order_id}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                        {order.items.length} item{order.items.length !== 1 ? "s" : ""}
                      </span>
                      <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                        {order.table_number ? `Table ${order.table_number}` : "PickUp"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Details - Compact */}
                <div className="p-4 space-y-3 flex-grow">
                  <div className="space-y-2">
                    {/* Customer and Status in separate rows */}
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-gray-600 font-medium text-sm">Customer: </span>
                        <span className="font-semibold text-gray-800 text-sm">
                          {order.customer_name}
                        </span>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-bold
                        ${
                          order.status === "Completed"
                            ? "bg-green-100 text-green-800"
                            : order.status === "Ready"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {order.status}
                      </span>
                    </div>
                    
                    {/* Other details in grid */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-medium">Total:</span>
                          <span className="font-bold text-green-600">
                            ${order.total_amount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-medium">Time:</span>
                          <span className="font-semibold text-orange-600 text-xs bg-orange-50 px-2 py-1 rounded">
                            {formatTimeDifference(order.updated_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Order Items - Compact */}
                  <div className="border-t border-gray-200 pt-2">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Order Items:
                    </h4>
                    <ul className="space-y-2 max-h-24 overflow-y-auto">
                      {order.items.map((item) => (
                        <li
                          key={item.menu_item_id}
                          className="flex justify-between items-center text-sm"
                        >
                          <span
                            className="text-gray-800 font-medium truncate flex-1 mr-2"
                            title={item.product_name}
                          >
                            {item.product_name}
                          </span>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-gray-600 bg-gray-100 px-2 py-1 rounded">
                              x{item.quantity}
                            </span>
                            <span className="font-semibold text-gray-700">
                              ${item.price.toFixed(2)}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Button - Compact */}
                  <div className="pt-2">
                    {order.status !== "Completed" &&
                      order.status !== "Cancelled" && (
                        <button
                          onClick={() =>
                            handleUpdateOrderStatus(order.order_id, 
                              order.status === "Ready" ? "Completed" : "Ready"
                            )
                          }
                          disabled={processingOrders.has(order.order_id)}
                          className={`w-full py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                            processingOrders.has(order.order_id)
                              ? "bg-gray-400 cursor-not-allowed"
                              : order.status === "Ready"
                              ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                              : "bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                          } text-white shadow-md hover:shadow-lg`}
                        >
                          {processingOrders.has(order.order_id)
                            ? "Processing..."
                            : order.status === "Ready"
                            ? "Mark as Completed"
                            : "Mark as Ready"}
                        </button>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Real-time Status Indicator - Compact */}
        {uniqueOrders.length > 0 && (
          <div className="mt-6 bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-gray-700">Live Updates Active</span>
              </div>
              {/* <div className="text-xs text-gray-500">
                {uniqueOrders.filter(order => order.status === "Pending").length} pending • 
                {uniqueOrders.filter(order => order.status === "Ready").length} ready • 
                {uniqueOrders.filter(order => order.status === "Completed").length} completed
              </div> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default KitchenManagement;